<!--
  DocumentPreview (Spec 118, 126) — one fitted page image at a time, paged with buttons / arrow
  keys, with "Open original" above. Falls back to DocumentThumbnail when there are
  no page images, the document is still processing or the lookup fails (no toast, E2).
-->
<script lang="ts">
  import { tick } from 'svelte';
  import { _ } from 'svelte-i18n';
  import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-svelte';
  import { api, type Document } from '../api';
  import { toastStore } from '../stores/toastStore';
  import { isInProcessing } from '../documentStatus';
  import { Button, Spinner } from './ui/index';
  import DocumentThumbnail from './DocumentThumbnail.svelte';

  export let document: Document;
  /** The detail page already has its own download button in the page header. */

  let pages: Array<{ page: number; url: string }> = [];
  let totalPages = 0;
  let loadedKey = '';
  let loading = true;
  let loaded: Record<number, boolean> = {};
  let refreshed = false;
  let index = 0;
  let rootEl: HTMLDivElement | undefined;
  let viewportEl: HTMLDivElement | undefined;
  const preloaded = new Set<string>();

  $: processing = isInProcessing(document);
  $: loadPages(document.id, processing);

  $: current = pages[Math.min(index, pages.length - 1)];
  // Indicator and alt text count up to the last page we actually have (Spec 126 E3).
  $: position = Math.min(index, pages.length - 1) + 1;
  $: if (pages[index + 1]) preload(pages[index + 1]);

  // Each URL is fetched once; a preloaded page counts as loaded, so paging shows no dark frame.
  function preload(next: { page: number; url: string }) {
    if (typeof Image === 'undefined' || preloaded.has(next.url)) return;
    preloaded.add(next.url);
    const key = loadedKey;
    const image = new Image();
    image.onload = () => {
      if (loadedKey === key) loaded[next.page] = true;
    };
    image.src = next.url;
  }

  async function go(step: number) {
    const hadFocus = !!rootEl?.contains(window.document.activeElement);
    index = Math.max(0, Math.min(pages.length - 1, index + step));
    await tick();
    // A button that turns disabled drops the focus; keep it in the preview so ←/→ keep working.
    // `document` is the prop here, hence window.document.
    const active = window.document.activeElement as HTMLButtonElement | null;
    if (hadFocus && (!active || active === window.document.body || active.disabled))
      viewportEl?.focus();
  }

  function onKeydown(e: KeyboardEvent) {
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    if ((e.target as HTMLElement).closest('input,textarea,select,[contenteditable]')) return;
    if (e.key === 'ArrowRight') go(1);
    else if (e.key === 'ArrowLeft') go(-1);
    else return;
    e.preventDefault();
  }

  async function loadPages(id: string, isProcessing: boolean) {
    const key = `${id}|${isProcessing}`;
    // A new document object with the same id must not reset the page.
    if (key === loadedKey) return;
    loadedKey = key;
    pages = [];
    totalPages = 0;
    loaded = {};
    preloaded.clear();
    refreshed = false;
    index = 0;
    loading = !isProcessing;
    if (isProcessing) return;
    try {
      const result = await api.documents.getPageImageUrls(id);
      if (loadedKey !== key) return;
      pages = result?.pages ?? [];
      totalPages = result?.totalPages ?? 0;
    } catch {
      // Fallback to the thumbnail; the preview is not the main thing of the page.
    } finally {
      if (loadedKey === key) loading = false;
    }
  }

  function onImageLoad(page: number) {
    loaded[page] = true;
    // The reloaded URLs work: a later expiry may reload once more instead of falling back.
    refreshed = false;
  }

  // Signed URLs expire: reload them once on an image error, then fall back to the thumbnail (E2).
  async function onImageError() {
    if (refreshed) {
      pages = [];
      return;
    }
    refreshed = true;
    const key = loadedKey;
    try {
      const result = await api.documents.getPageImageUrls(document.id);
      if (loadedKey !== key) return;
      loaded = {};
      preloaded.clear();
      pages = result?.pages ?? [];
    } catch {
      if (loadedKey === key) pages = [];
    }
  }

  // The tab must open inside the click (popup blocker); the signed URL is fetched afterwards.
  let opening = false;
  async function openOriginal() {
    if (opening) return;
    opening = true;
    const tab = window.open('', '_blank');
    try {
      const url = await api.documents.getDownloadSignedUrl(document.id);
      if (!tab) {
        window.location.assign(url);
        return;
      }
      tab.opener = null;
      tab.location.href = url;
    } catch {
      tab?.close();
      toastStore.error($_('components.document_detail.open_error'));
    } finally {
      opening = false;
    }
  }
</script>

<!-- Key handling sits on the root so ←/→ also work while a pager button has the focus. -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="flex flex-1 min-h-0 flex-col gap-2" bind:this={rootEl} on:keydown={onKeydown}>
  <!-- PO 2026-10-09 (Spec 118 E4): one action only — the download URL is inline (Spec 110), a
       separate "Download" button would open the same tab. -->
  <div class="flex shrink-0 justify-end gap-1">
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      disabled={opening}
      on:click={openOriginal}
      aria-label={$_('components.document_preview.open_original')}
      title={$_('components.document_preview.open_original')}
      data-testid="document-preview-open-original"
    >
      <ExternalLink class="w-4 h-4" strokeWidth={1.5} />
    </Button>
  </div>

  {#if current}
    <!-- The page area takes what the host column leaves (a flex column of definite height beside
         the form); in a stacked host without a height it is the natural full-width page. Focusable
         so ←/→ page from the page area. -->
    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <div
      bind:this={viewportEl}
      class="flex flex-1 min-h-0 items-center justify-center rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
      tabindex="0"
      role="group"
      aria-label={document.fileName}
      data-testid="document-preview-viewport"
    >
      <img
        src={current.url}
        alt={$_('components.document_preview.page_alt', {
          values: { page: position, total: pages.length, name: document.fileName },
        })}
        on:load={() => onImageLoad(current.page)}
        on:error={onImageError}
        class="border border-border rounded object-contain {loaded[current.page]
          ? 'bg-white h-auto w-auto max-w-full max-h-full'
          : 'bg-bg-alt w-full aspect-[210/297] max-h-full'}"
        data-testid="document-preview-page-{current.page}"
      />
    </div>
    {#if pages.length > 1}
      <div
        class="flex shrink-0 items-center justify-center gap-2"
        data-testid="document-preview-pager"
      >
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          class="max-sm:w-11 max-sm:h-11"
          disabled={index === 0}
          on:click={() => go(-1)}
          aria-label={$_('components.document_preview.previous_page')}
          title={$_('components.document_preview.previous_page')}
        >
          <ChevronLeft class="w-4 h-4" strokeWidth={1.5} />
        </Button>
        <span
          class="text-xs text-text-muted tabular-nums"
          aria-hidden="true"
          data-testid="document-preview-indicator"
        >
          {position} / {pages.length}
        </span>
        <span class="sr-only" aria-live="polite" aria-atomic="true">
          {$_('components.document_preview.page_indicator', {
            values: { page: position, total: pages.length },
          })}
        </span>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          class="max-sm:w-11 max-sm:h-11"
          disabled={index === pages.length - 1}
          on:click={() => go(1)}
          aria-label={$_('components.document_preview.next_page')}
          title={$_('components.document_preview.next_page')}
        >
          <ChevronRight class="w-4 h-4" strokeWidth={1.5} />
        </Button>
      </div>
    {/if}
    {#if totalPages > pages.length}
      <p class="shrink-0 text-xs text-text-muted" data-testid="document-preview-range-note">
        {$_('components.document_preview.page_range', {
          values: { from: pages[0].page, to: pages[pages.length - 1].page, total: totalPages },
        })}
        ·
        <button
          type="button"
          class="text-accent hover:underline cursor-pointer bg-transparent border-none p-0"
          data-testid="document-preview-open-original-link"
          on:click={openOriginal}
        >
          {$_('components.document_preview.open_original')}
        </button>
      </p>
    {/if}
  {:else if !loading}
    <DocumentThumbnail {document} size="large" />
  {:else}
    <div
      class="flex w-full aspect-[210/297] max-h-full items-center justify-center rounded border border-border bg-bg-alt"
    >
      <Spinner size="md" />
    </div>
  {/if}
</div>
